/**
 * Session expiry (mission 6 review follow-up 14, A-1; A.5, FR-021;
 * contracts/session-and-gate.md "Frontend routing").
 *
 * After idle expiry every /app screen used to answer a poll, a start, a save,
 * a download or an export with the generic error, and retrying never worked.
 * `installSessionExpiry` registers the API's problem listener: on a 401
 * `unauthorized` (the one body for every session failure, problem.go) while
 * the reporter is on an /app route, it drops the cached identity and replaces
 * the page with /login, carrying the return path and `sessionExpired` in router
 * state. Nothing goes in a query string, which could carry filters or ids into
 * logs and history.
 *
 * - Every cached query is dropped before the navigation (AuthGate
 *   `clearSessionCache`): /login's inverted gate refetches instead of bouncing
 *   back to the app on the stale session, and no tenant-scoped query (credits,
 *   runs, header parameters, run views) survives for the next reporter who
 *   signs in in this tab (TEN.1; cycle 2 finding 1).
 * - Other 401 types (`invalid-credentials`, `invalid-code`) are a screen's own
 *   answer, and so is any 401 outside /app: the auth screens handle theirs.
 * - The return path is consumed after the sign-in (FR-026; follow-up 14; squad
 *   U26): Login carries `from` on to /mfa as `returnTo`, in router state only,
 *   and every exit from /mfa into the app goes through `safeReturnPath`.
 *
 * The same listener refreshes the shell's billing state (mission 9 review UI1; FR-015, B.1): a 402
 * `billing-read-only` means the access the shell last read is stale (day 8 or the period end arrived while
 * the page was open), so it invalidates the credits read the banner, the chip and every gate observe. The
 * refused action already shows its own band; nothing navigates.
 */
import type { QueryClient } from '@tanstack/react-query'

import { APP_HOME, clearSessionCache } from '@/components/trellis/AuthGate'
import { setProblemListener, type Problem } from './api'
import { PROBLEM } from './problems'
import { invalidateCredits } from './queries'

/** What /login receives in router state. */
export interface LoginState {
  /** The path, search and hash the reporter was on. */
  from: string
  sessionExpired: true
}

/** The slice of a data router the handler reads and drives. */
export interface SessionRouter {
  state: { location: { pathname: string; search: string; hash: string } }
  navigate(to: string, opts: { replace: boolean; state: LoginState }): unknown
}

function inApp(pathname: string): boolean {
  return pathname === '/app' || pathname.startsWith('/app/')
}

/**
 * Where a sign-in may land (FR-026; squad U26): `returnTo` resolved against
 * `origin` with `new URL`, kept only when it stays on `origin` and its path is
 * `/app` or under `/app/`; its path, search and hash are returned. Anything
 * else, including a value that is not a string or does not parse, is APP_HOME.
 * Resolving first is what closes the open redirect: `//host`, `/\host`,
 * `https://host` and dot segments are judged by where the browser would go,
 * not by how the string begins.
 */
export function safeReturnPath(returnTo: unknown, origin: string): string {
  if (typeof returnTo !== 'string') return APP_HOME
  let url: URL
  try {
    url = new URL(returnTo, origin)
  } catch {
    return APP_HOME
  }
  if (url.origin !== origin || !inApp(url.pathname)) return APP_HOME
  return `${url.pathname}${url.search}${url.hash}`
}

/** True for the session-failure body, and only that one. */
export function isSessionExpired(problem: Problem): boolean {
  return problem.type === PROBLEM.UNAUTHORIZED && problem.status === 401
}

/** True for the read-only refusal (B.1), and only that one. */
function isBillingReadOnly(problem: Problem): boolean {
  return problem.type === PROBLEM.BILLING_READ_ONLY && problem.status === 402
}

/** Installs the handler; returns the uninstaller. */
export function installSessionExpiry(router: SessionRouter, client: QueryClient): () => void {
  return setProblemListener((problem) => {
    if (isBillingReadOnly(problem)) {
      void invalidateCredits(client)
      return
    }
    if (!isSessionExpired(problem)) return
    const { pathname, search, hash } = router.state.location
    if (!inApp(pathname)) return
    clearSessionCache(client)
    void router.navigate('/login', { replace: true, state: { from: `${pathname}${search}${hash}`, sessionExpired: true } })
  })
}
